const { min, max } = Math;
const File = globalThis.File ?? function () {}; // `File` not supported in node 18

export type CSVRow = string[];
export type CSVEntry = { [column: string]: string };

export class CSV {
  #headers: CSVRow = [];
  #data: CSVRow[] = [];

  #cellString(value: unknown): string {
    if (value == null) return "";
    if (typeof value === "string") return value;
    if (typeof value === "symbol") return String(value);
    try {
      return JSON.stringify(value);
    } catch (_e) {
      return String(value);
    }
  }
  #normalizeRow(row: CSVRow): CSVRow {
    const normRow: CSVRow = [];
    for (let i = 0; i < this.#headers.length; i++) {
      normRow.push(this.#cellString(row[i]));
    }
    return normRow;
  }
  #escapeCell(cell: string, separator: string): string {
    cell = this.#cellString(cell);
    return /[\r\n"]/.test(cell) || cell.includes(separator)
      ? `"${cell.replace(/"/g, '""')}"`
      : cell;
  }
  #rowString(row: CSVRow, separator: string): string {
    return (
      row.map((cell) => this.#escapeCell(cell, separator)).join(separator) +
      "\n"
    );
  }
  static #getFirstLine(str: string) {
    // match any unquoted characters, and quoted sections, until a newline or EOF is reached
    return str.match(/^((?:[^\n"]|"([^"]|"")*")*)(?:\n|$)/)?.[1] ?? null;
  }
  static #inferSeparatorFromBlob(separator: string | null, blob: Blob) {
    // if separator isn't given, and the file extension is tsv, assume tab
    if (
      separator == null &&
      blob instanceof File &&
      /\.tsv/i.test((blob as File).name)
    ) {
      separator = "\t"; // tsv handling
    }
    return separator;
  }
  static #inferSeparatorFromContents(str: string) {
    // get header line, and remove quoted parts
    const firstLine = this.#getFirstLine(str)?.replace(/"([^"]|"")*"/g, "");
    // if header line failed to match, the csv is likely invalid; just assume `,`
    if (!firstLine) return ",";

    if (firstLine.includes(";")) {
      // if unquoted regions contain a semi-colon, it's most likely semi-colon separated
      return ";";
    }
    if (firstLine.includes("\t")) {
      // if not, but they contain a tab, it's most likely tab-separated
      return "\t";
    }
    if (firstLine.includes(",")) {
      // if not, but they contain a comma, it's most likely comma-separated
      return ",";
    }
    // if nothing else, it's probably one column; default to comma
    return ",";
  }

  /** Construct a new CSV from a list of headers. Data can be added after.
   * For parsing a CSV from a string, or building CSVs from other data structures,
   * see the static methods, `CSV.fromString`, `CSV.fromBlob`, `CSV.fromColumnMajor`. */
  constructor(columns: CSVRow) {
    this.#headers = [...columns].map(this.#cellString); // force-cast to strings
  }
  /** CSV Width / Number of columns in the CSV */
  get width(): number {
    return this.#headers.length;
  }
  /** CSV Height / Number of rows in the CSV (excluding headers row) */
  get height(): number {
    return this.#data.length;
  }
  /** CSV Width / Number of columns in the CSV */
  get columns(): number {
    return this.#headers.length;
  }
  /** CSV Width / Number of columns in the CSV */
  get columnCount(): number {
    return this.#headers.length;
  }
  /** CSV Height / Number of rows in the CSV (excluding headers row) */
  get rowCount(): number {
    return this.#data.length;
  }
  /** Add a row of values (array of strings) to the CSV before a given index. Use -1 (default) to add to end. 'position' excludes the headers row. */
  addRow(row: CSVRow, position: number = -1): this {
    row = this.#normalizeRow(row);
    if (position !== position >>> 0 || position >= this.#data.length) {
      this.#data.push(row);
    } else {
      this.#data.splice(position, 0, row);
    }
    return this;
  }
  /** Add an entry (object mapping headers to values) to the CSV before a given index. Use -1 (default) to add to end. 'position' excludes the headers row. */
  addEntry(entry: CSVEntry, position: number = -1): this {
    const row = [];
    for (let i = 0; i < this.#headers.length; i++) {
      row[i] = this.#cellString(entry[this.#headers[i]]);
    }
    if (position !== position >>> 0 || position >= this.#data.length) {
      this.#data.push(row);
    } else {
      this.#data.splice(position, 0, row);
    }
    return this;
  }
  /** Remove a row at the given position from the CSV. 'position' excludes the headers row. */
  removeRow(position: number): CSVRow {
    const row = this.#data.splice(position, 1);
    return row[0];
  }
  /** Clean the data. Trims leading and trailing whitespace from headers and cells, and removes empty rows. */
  cleanData(): this {
    // trim headers
    for (let i = 0; i < this.#headers.length; i++) {
      this.#headers[i] = this.#headers[i].replace(/^\s+|\s+$/g, "");
    }
    // clean rows
    for (let i = 0; i < this.#data.length; i++) {
      const row = this.#data[i];
      let empty = true;

      // trim row cells
      for (let j = 0; j < row.length; j++) {
        row[j] = row[j].replace(/^\s+|\s+$/g, "");
        if (row[j]) empty = false;
      }

      // if only empty values were detected in row after trimming, remove row
      if (empty) this.#data.splice(i--, 1);
    }
    return this;
  }
  /** Format headers via a callback. Allows for things like lowercasing or camel-casing headers. */
  formatHeaders(callback: (header: string) => string): this {
    for (let i = 0; i < this.#headers.length; i++) {
      this.#headers[i] = callback(this.#headers[i]);
    }
    return this;
  }
  /** Format cells via a callback. Allows for things like lowercasing or converting cell aliases. */
  formatCells(callback: (cell: string, column: string) => string): this {
    for (let i = 0; i < this.#data.length; i++) {
      const row = this.#data[i];
      for (let j = 0; j < row.length; j++) {
        row[j] = this.#cellString(callback(row[j], this.#headers[j]));
      }
    }
    return this;
  }
  /** Clear all rows from the CSV. Headers are preserved. */
  clear(): this {
    this.#data.length = 0;
    return this;
  }
  /** Get the array of headers from the CSV as a string array. */
  getHeaders(): CSVRow {
    return this.#headers;
  }
  /** Get a row of values (array of strings) at the given index from the CSV. */
  getRow(position: number): CSVRow | null {
    if (position !== position >>> 0 || position >= this.#data.length) {
      return null;
    }
    return this.#data[position];
  }
  /** Get an entry (object mapping headers to values) at the given index from the CSV. */
  getEntry(position: number): CSVEntry | null {
    if (position !== position >>> 0 || position >= this.#data.length) {
      return null;
    }
    const row = this.#data[position] ?? [];
    const entry = Object.create(null) as CSVEntry;
    for (let i = 0; i < this.#headers.length; i++) {
      entry[this.#headers[i]] = row[i];
    }
    return entry;
  }
  /** Iterate all rows in the CSV (arrays of strings). */
  *rows(): Generator<CSVRow, undefined, undefined> {
    yield* this.#data;
  }
  /** Iterate all entries in the CSV (objects mapping headers to values). */
  *entries(): Generator<CSVEntry, undefined, undefined> {
    for (const row of this.#data) {
      const entry = Object.create(null) as CSVEntry;
      for (let i = 0; i < this.#headers.length; i++) {
        entry[this.#headers[i]] = row[i];
      }
      yield entry;
    }
  }
  /** Return a copy of the CSV. */
  clone(): CSV {
    const csv = new CSV(this.#headers);
    csv.#data = this.#data.map((row) => row.slice());
    return csv;
  }
  /** Convert the CSV back to a file-storable string. */
  toString(separator: string = ","): string {
    let csv = this.#rowString(this.#headers, separator);
    for (const row of this.#data) csv += this.#rowString(row, separator);
    return csv;
  }
  /** Convert the CSV to a column-major hashtable (an object mapping each header to an array of values for that column). */
  toColumnMajor(): { [column: string]: string[] } {
    const columns = new Array(this.#headers.length)
      .fill(null)
      .map(() => []) as string[][];
    for (const row of this.#data) {
      for (let i = 0; i < columns.length; i++) {
        columns[i].push(row[i]);
      }
    }
    const data = Object.create(null) as { [column: string]: string[] };
    for (let i = 0; i < columns.length; i++) {
      data[this.#headers[i]] = columns[i];
    }
    return data;
  }

  /** Parses a CSV string and returns a new CSV object.
   * Pass the separator string, or null for automatic separator detection.
   * When run in strict mode (default), errors will be thrown for CSV syntax errors. Otherwise, CSV will be force-parsed. */
  static fromString(
    str: string,
    separator: string | null = null,
    strict: boolean = true
  ): CSV {
    let c: number = 0;

    // try detecting the separator
    if (separator == null) {
      separator = CSV.#inferSeparatorFromContents(str);
    }

    const readCell = () => {
      let cell = "";
      if (str[c] === '"') {
        c++; // skip open quote
        while (c < str.length) {
          if (str[c] === '"' && str[c + 1] === '"') {
            cell += '"';
            c += 2;
            continue;
          }
          if (str[c] === '"') break;
          cell += str[c];
          c++;
        }
        if (strict && str[c] !== '"') {
          throw new Error(
            `CSV Parse Error: Quoted cell missing close quote at position ${c}`
          );
        }
        c++; // skip close quote
        if (
          strict &&
          c < str.length &&
          str[c] !== separator &&
          str[c] !== "\r" &&
          str[c] !== "\n"
        ) {
          throw new Error(
            `CSV Parse Error: Cell data continues after endquote at position ${c}`
          );
        }
      }
      while (c < str.length) {
        if (str[c] === separator) break;
        if (str[c] === "\n" || (str[c] === "\r" && str[c + 1] === "\n")) break;
        cell += str[c];
        c++;
      }
      return cell;
    };
    const readRow = () => {
      const row = [];
      while (c < str.length) {
        row.push(readCell());

        if (str[c] === separator) {
          c++;
        } else if (c < str.length) {
          if (str[c] === "\r") c++;
          if (str[c] === "\n") {
            c++;
            break;
          }
          if (strict)
            throw new Error(
              `CSV Parse Error: Unexpected character '${str[c]}' at position ${c}`
            );
        }
      }
      return row;
    };
    const readCSV = () => {
      const headers = readRow();
      const csv = new CSV(headers);
      while (c < str.length) {
        const row = readRow();
        if (row.length !== headers.length) {
          if (strict) {
            throw new Error(
              `CSV Parse Error: Length of row ${csv.#data.length} does not match header length`
            );
          } else {
            // extend
            while (row.length < headers.length) {
              row.push("");
            }
            // truncate
            row.length = headers.length;
          }
        }
        csv.addRow(row);
      }
      return csv;
    };

    return readCSV();
  }
  /** Parses a column-major hashtable (an object mapping each header to an array of values for that column) and returns a new CSV object. */
  static fromColumnMajor(data: { [column: string]: string[] }): CSV {
    const headers = Object.keys(data);
    const rowCount = max(...headers.map((column) => data[column].length));
    const csv = new CSV(headers);

    for (let i = 0; i < rowCount; i++) {
      const row = headers.map((column) => data[column][i] ?? "");
      csv.addRow(row);
    }
    return csv;
  }
  /** Generates a CSV from an array of entries. */
  static fromEntries(data: CSVEntry[]): CSV {
    const headers = new Set<string>();
    for (const entry of data) {
      for (const prop in entry) {
        headers.add(prop);
      }
    }
    const csv = new CSV([...headers]);
    for (const entry of data) {
      csv.addEntry(entry);
    }
    return csv;
  }
  /** Reads a Blob or File object, parses it as a CSV string, and returns a new CSV object.
   * Pass the separator string, or null for automatic separator detection.
   * When run in strict mode (default), errors will be thrown for CSV syntax errors. Otherwise, CSV will be force-parsed. */
  static async fromBlob(
    blob: Blob,
    separator: string | null = null,
    strict: boolean = true
  ) {
    separator = CSV.#inferSeparatorFromBlob(separator, blob);
    return CSV.fromString(await blob.text(), separator, strict);
  }

  /** Reads a CSV of just the headers from the given Blob or File object. */
  static async headersFromBlob(
    blob: Blob,
    separator: string | null = null,
    strict: boolean = true,
    maxHeaderRowSize = 1024 * 1024 // 1 MB
  ): Promise<CSV> {
    separator = CSV.#inferSeparatorFromBlob(separator, blob);

    let str = "";
    const end = min(maxHeaderRowSize, blob.size);
    for (let i = 0; i < end; i += 1024) {
      // increase blob size by 1KB increments
      str = await blob.slice(0, i + 1024).text();

      const firstLine = this.#getFirstLine(str);
      if (firstLine) {
        return CSV.fromString(firstLine, separator, strict);
      }
    }

    if (strict) {
      throw new Error("Failed to match a valid header from the blob.");
    }

    return new CSV([]);
  }
}
